// Canonical onboarding state model + human-readable English labels.
// This is the single source of truth the Back Office uses to render states and
// to decide editability — raw enum identifiers must never reach the screen
// (FR-024), and the edit lock (FR-015a/FR-016) is derived from PIPELINE order.

import { OnboardingState, SignMethod } from "../types/onboarding";

// Ordered pipeline states (index defines progression order).
export const PIPELINE_STATES: OnboardingState[] = [
  "ACCOUNT_CREATED",
  "FORM_SUBMITTED",
  "STATION_ID_ASSIGNED",
  "CONTRACT_DRAFT_CREATED",
  "CONTRACT_SENT",
  "CONTRACT_SIGNED",
  "STATION_ADDED_TO_EROUND",
  "OCPP_CREDENTIALS_GENERATED",
  "CUSTOMER_PROMOTED",
  "OCPP_CREDENTIALS_SENT",
  // The credentials e-mail carries the welcome box, so the backend fires this
  // transition from the same call — and a RESEND from OCPP_CREDENTIALS_SENT
  // advances into it (ConfigDeliveryService). It is therefore the state a
  // customer actually rests in while they wire up their wallbox.
  "WELCOME_BOX_SENT",
  // Every station has reported itself available over OCPI; the backend advances
  // here on its own (StationAvailabilityService) and ops publish next. Ops wait
  // in this state rather than drive it.
  "STATION_AVAILABLE",
  // eRound has published the location — the go-live milestone. This is the
  // "live" threshold the backend and the analytics dashboards key off, and it
  // replaces the retired STATION_ONLINE.
  "STATION_PUBLIC",
  "FIRST_SESSION_CHARGED",
  "ONBOARDING_COMPLETED",
];

// Special (non-pipeline) states.
export const MORE_INFO_NEEDED: OnboardingState = "MORE_INFO_NEEDED";
export const RESIGNED: OnboardingState = "RESIGNED";

// State at/after which a prospect's fields become read-only in the Back Office.
const EDIT_LOCK_STATE: OnboardingState = "CONTRACT_SENT";

export const STATE_LABELS: Record<OnboardingState, string> = {
  ACCOUNT_CREATED: "Account created",
  FORM_SUBMITTED: "Form submitted",
  STATION_ID_ASSIGNED: "Station ID assigned",
  CONTRACT_DRAFT_CREATED: "Contract draft created",
  CONTRACT_SENT: "Contract sent",
  ESIGN_AWAITED: "Awaiting e-signature",
  CONTRACT_SIGNED: "Contract signed",
  STATION_ADDED_TO_EROUND: "Station added to billing",
  OCPP_CREDENTIALS_GENERATED: "Access data confirmed",
  CUSTOMER_PROMOTED: "Customer promoted",
  OCPP_CREDENTIALS_SENT: "Credentials sent",
  WELCOME_BOX_SENT: "Welcome box sent",
  STATION_AVAILABLE: "Station available",
  STATION_PUBLIC: "Station public",
  FIRST_SESSION_CHARGED: "First session charged",
  ONBOARDING_COMPLETED: "Onboarding completed",
  MORE_INFO_NEEDED: "More info needed",
  RESIGNED: "Resigned",
};

export const stateLabel = (state: OnboardingState): string =>
  STATE_LABELS[state] ?? state;

type Severity = "info" | "success" | "warning" | "danger" | null;

// The status pill's colour carries the one thing an agent needs at a glance:
// whether this prospect is moving, waiting on someone, finished, or gone.
// Deliberately four buckets rather than a colour per state — fifteen colours
// would read as decoration instead of as a signal.
const LIVE_STATES: OnboardingState[] = [
  "STATION_PUBLIC",
  "FIRST_SESSION_CHARGED",
  "ONBOARDING_COMPLETED",
];

export const stateSeverity = (state: OnboardingState): Severity => {
  if (state === RESIGNED) return "danger";
  if (state === MORE_INFO_NEEDED) return "warning";
  if (LIVE_STATES.includes(state)) return "success";
  return "info";
};

// Index of a state within the pipeline; -1 for the special states.
export const pipelineIndex = (state: OnboardingState): number =>
  PIPELINE_STATES.indexOf(state);

export const isPipelineState = (state: OnboardingState): boolean =>
  pipelineIndex(state) !== -1;

export const isTerminal = (state: OnboardingState): boolean =>
  state === RESIGNED;

// Pipeline states strictly after the current one (forward-only advance).
// Returns [] for the special states (RESIGNED is terminal; MORE_INFO_NEEDED is
// an overlay and never the currentState).
export const nextPipelineStates = (
  current: OnboardingState
): OnboardingState[] => {
  const idx = pipelineIndex(current);
  if (idx === -1) return [];
  return PIPELINE_STATES.slice(idx + 1);
};

// Editable while strictly before the edit-lock state. RESIGNED and other
// special states are never editable.
export const isEditableState = (state: OnboardingState): boolean => {
  const idx = pipelineIndex(state);
  if (idx === -1) return false;
  return idx < pipelineIndex(EDIT_LOCK_STATE);
};

// Contract draft generation is permitted once station IDs are assigned.
export const canGenerateContractDraft = (state: OnboardingState): boolean =>
  state === "STATION_ID_ASSIGNED";

// A draft already exists at this state — ops may re-generate.
export const isDraftCreated = (state: OnboardingState): boolean =>
  state === "CONTRACT_DRAFT_CREATED";

// ---- Contract signing (CWA-78) ----
// ESIGN_AWAITED is a branch state (digital track) and deliberately NOT in
// PIPELINE_STATES, so pipelineIndex()/isEditableState() treat it as non-pipeline
// (non-editable) automatically. Signing gating uses the explicit helpers below.

// States where the contract-signing panel is shown (from draft to signed).
export const isSigningStage = (state: OnboardingState): boolean =>
  state === "CONTRACT_DRAFT_CREATED" ||
  state === "CONTRACT_SENT" ||
  state === "ESIGN_AWAITED" ||
  state === "CONTRACT_SIGNED";

// The one state from which a contract can be sent (any method).
export const canSendContract = (state: OnboardingState): boolean =>
  state === "CONTRACT_DRAFT_CREATED";

export const isEsignAwaited = (state: OnboardingState): boolean =>
  state === "ESIGN_AWAITED";

// Awaiting the prospect to sign (drives reminders + overdue cue).
export const awaitingSignature = (state: OnboardingState): boolean =>
  state === "CONTRACT_SENT" || state === "ESIGN_AWAITED";

// States where the contract panel and its activity are worth showing. A
// contract record exists from the draft onward and stays relevant for the rest
// of the journey — an agent may need to check what was signed long after the
// station went live — so this is deliberately wider than isSigningStage, which
// gates the send/record ACTIONS.
export const hasContractHistory = (state: OnboardingState): boolean => {
  if (state === "ESIGN_AWAITED") return true;
  const idx = pipelineIndex(state);
  return idx !== -1 && idx >= pipelineIndex("CONTRACT_DRAFT_CREATED");
};

// The contract's own status, which is not the same as the prospect's stage:
// once the journey moves past signing the contract is simply signed, and
// showing "Station public" inside the contract panel would read as if that were
// the contract's state. Assumes hasContractHistory(state).
export const contractStatusLabel = (state: OnboardingState): string =>
  isSigningStage(state) ? stateLabel(state) : stateLabel("CONTRACT_SIGNED");

export type ContractSendAction = "send" | "esign-sent" | "hub-sign";
export type ContractRecordAction = "upload-signed" | "confirm-signed";

// The single send action offered at CONTRACT_DRAFT_CREATED for a method.
export const sendActionFor = (
  method: SignMethod | null | undefined
): ContractSendAction | null => {
  switch (method) {
    case "MANUAL":
      return "send";
    case "DIGITAL":
      return "esign-sent";
    case "CLICK_TO_SIGN":
      return "hub-sign";
    default:
      return null;
  }
};

// ---- Station setup & configuration delivery (CWA-91) ----
// Each helper mirrors a state guard that was read off the backend, so the UI
// only ever offers an action the API will accept. Sources are noted per helper;
// the full map is in specs/005-station-config-delivery/contracts/.

// The station configuration panel is shown from CONTRACT_SIGNED onwards. Uses
// pipeline order so every later stage is included automatically, and the
// non-pipeline states (ESIGN_AWAITED, MORE_INFO_NEEDED, RESIGNED) are excluded.
export const isStationConfigStage = (state: OnboardingState): boolean => {
  const idx = pipelineIndex(state);
  return idx !== -1 && idx >= pipelineIndex("CONTRACT_SIGNED");
};

// StationCredentialsService.registerStations accepts exactly these two.
export const canRegisterStations = (state: OnboardingState): boolean =>
  state === "CONTRACT_SIGNED" || state === "STATION_ADDED_TO_EROUND";

// ProspectService.promoteToCustomer accepts these three — note it allows
// promoting straight from CONTRACT_SIGNED, so OCPP_CREDENTIALS_GENERATED is
// routinely skipped.
export const canPromoteToCustomer = (state: OnboardingState): boolean =>
  state === "CONTRACT_SIGNED" ||
  state === "STATION_ADDED_TO_EROUND" ||
  state === "OCPP_CREDENTIALS_GENERATED";

// ConfigDeliveryService.sendConfigEmail accepts these three. Only the first is
// the initial send; the other two are resends. Note a resend no longer leaves
// the state untouched — from OCPP_CREDENTIALS_SENT the backend advances the
// prospect to WELCOME_BOX_SENT (spec 0040), so callers must refetch.
export const canSendConfigEmail = (state: OnboardingState): boolean =>
  state === "CUSTOMER_PROMOTED" ||
  state === "OCPP_CREDENTIALS_SENT" ||
  state === "WELCOME_BOX_SENT";

// True once the configuration email has gone out, so the action is a resend.
export const isConfigEmailSent = (state: OnboardingState): boolean =>
  state === "OCPP_CREDENTIALS_SENT" || state === "WELCOME_BOX_SENT";

// ---- Station go-live (CWA-94) ----
// GoLiveService.markAsLive accepts OCPP_CREDENTIALS_SENT, WELCOME_BOX_SENT and
// STATION_AVAILABLE (each advances the prospect to STATION_PUBLIC) and
// STATION_PUBLIC (resends the e-mail, no transition); everything else is
// refused with a 409. The two cases are separate helpers because they drive two
// differently-worded actions.

export const canMarkLive = (state: OnboardingState): boolean =>
  state === "OCPP_CREDENTIALS_SENT" ||
  state === "WELCOME_BOX_SENT" ||
  state === "STATION_AVAILABLE";

export const canResendGoLiveEmail = (state: OnboardingState): boolean =>
  state === "STATION_PUBLIC";

// ---- Publication (CWA-154) ----
// The stations have reported themselves available and the prospect is waiting on
// an agent to publish. The backend reaches this state on its own, off the OCPI
// availability report (spec 0040 renamed it from STATION_READY_TO_PUBLISH).
export const isReadyToPublish = (state: OnboardingState): boolean =>
  state === "STATION_AVAILABLE";

// The four states the backend accepts for go-live / confirm-publication — the
// two endpoints share one handler, so they share these states (contract §3).
// OCPP_CREDENTIALS_SENT and WELCOME_BOX_SENT are deliberate: an availability
// signal that never arrived must not block an agent who can see the station is
// up.
export const canConfirmPublication = (state: OnboardingState): boolean =>
  state === "STATION_AVAILABLE" ||
  state === "OCPP_CREDENTIALS_SENT" ||
  state === "WELCOME_BOX_SENT" ||
  state === "STATION_PUBLIC";

// Which endpoint the publish action should call, and therefore what to call the
// button. Both endpoints do the same thing; picking by stage keeps the audit
// trail honest about what the agent was actually doing (research R9). null at
// STATION_PUBLIC — that is the resend path, gated by canResendGoLiveEmail.
export type PublishAction = "confirm-publication" | "go-live";

export const publishActionFor = (
  state: OnboardingState
): PublishAction | null => {
  if (isReadyToPublish(state)) return "confirm-publication";
  if (state === "OCPP_CREDENTIALS_SENT" || state === "WELCOME_BOX_SENT") {
    return "go-live";
  }
  return null;
};

// True once the prospect's stations are public. Note this is a property of the
// prospect: the backend derives every station's status from it.
export const isStationLive = (state: OnboardingState): boolean =>
  state === "STATION_PUBLIC";

// The journey is finished, so the customer may rate it (CWA-207).
export const isOnboardingCompleted = (state: OnboardingState): boolean =>
  state === "ONBOARDING_COMPLETED";

// What the operations team has to do next at this stage, in plain English.
// Returns null where nothing is owed — a finished or an ended onboarding.
export const nextActionFor = (state: OnboardingState): string | null => {
  switch (state) {
    case "ACCOUNT_CREATED":
      return "Wait for the customer to submit their onboarding form.";
    case "FORM_SUBMITTED":
      return "Assign the station IDs for this prospect.";
    case "STATION_ID_ASSIGNED":
      return "Generate the contract draft.";
    case "CONTRACT_DRAFT_CREATED":
      return "Send the contract to the customer.";
    case "CONTRACT_SENT":
    case "ESIGN_AWAITED":
      return "Wait for the customer to sign, then record the signed contract.";
    case "CONTRACT_SIGNED":
      return "Check the station access data, then promote the prospect to a customer.";
    case "STATION_ADDED_TO_EROUND":
    case "OCPP_CREDENTIALS_GENERATED":
      return "Promote the prospect to a customer.";
    case "CUSTOMER_PROMOTED":
      return "Send the configuration email.";
    case "OCPP_CREDENTIALS_SENT":
    case "WELCOME_BOX_SENT":
      return "Wait for the station to report itself available — or, once you have published the location in eRound, mark it live here.";
    case "STATION_AVAILABLE":
      return "Make the location publicly visible in eRound, then confirm the publication here.";
    case "STATION_PUBLIC":
      return "Nothing to do — waiting for the customer's first charging session.";
    case "FIRST_SESSION_CHARGED":
      return "Nothing to do — the onboarding closes on its own.";
    case "MORE_INFO_NEEDED":
      return "Ask the customer for the missing information.";
    default:
      // ONBOARDING_COMPLETED and RESIGNED owe nothing.
      return null;
  }
};

// The ops record action for a method (null for CLICK_TO_SIGN — the customer
// completes signing in the Hub, so there is no ops upload/confirm step).
export const recordActionFor = (
  method: SignMethod | null | undefined
): ContractRecordAction | null => {
  switch (method) {
    case "MANUAL":
      return "upload-signed";
    case "DIGITAL":
      return "confirm-signed";
    default:
      return null;
  }
};
